import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, map, Observable, startWith, Subscription } from 'rxjs';
import { Acl } from 'src/app/models/acl';
import { EnumAffinities } from 'src/app/models/enum-affinities';
import { EnumScopes } from 'src/app/models/enum-scopes';
import { ExternalParameters } from 'src/app/models/external-parameters';
import { RightDefinition } from 'src/app/models/right-definition';
import { ExternalsParametersService } from 'src/app/services/externals-parameters.service';
import { RightDefinitonsService } from 'src/app/services/right-definitons.service';
import { UsersService } from 'src/app/services/users.service';
import { FormArrayFilterPipePipe } from 'src/app/pipes/form-array-filter-pipe.pipe';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatCard, MatCardContent } from '@angular/material/card';
import { AsyncPipe } from '@angular/common';
import { DialogAddRightsComponent } from '../dialogs/dialog-add-rights/dialog-add-rights.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

export class RightItem {
    name: string;
    display: string;
    isSelected: boolean;
}
@Component({
    selector: 'app-rights',
    templateUrl: './rights.component.html',
    styleUrls: ['./rights.component.css'],
    imports: [
        MatCard,
        MatCardContent,
        MatFormField,
        MatLabel,
        MatInput,
        ReactiveFormsModule,
        MatCheckbox,
        MatIconButton,
        MatTooltip,
        MatIcon,
        FormsModule,
        MatSuffix,
        MatSlideToggle,
        TranslateModule,
        FormArrayFilterPipePipe,
    ],
})
export class RightsComponent implements OnInit, OnDestroy {
    rightsForm: UntypedFormGroup;
    availableRights: RightDefinition[] = [];
    unassignedRights: Array<RightItem> = [];
    selectedRight: string[] = [];
    subscriptionEvents: Subscription;
    modifiedRights: Acl[] = [];
    rightListControl = new FormControl();
    rightList: Observable<RightItem[]>;

    searchRight = '';

    @Input() rights: Acl[];

    @Input() readonly: boolean;

    @Input() affinity: EnumAffinities = EnumAffinities.Any;

    @Output() changed = new EventEmitter<Acl[]>();

    @ViewChild('allSelected') private allSelected: MatOption;

    @ViewChild('rightInput') propertyInput: ElementRef<HTMLInputElement>;

    isExternal = false;
    externalParameters: ExternalParameters;
    externalParametersChange: Subscription;

    AddRightsDialog: MatDialogRef<DialogAddRightsComponent>;

    constructor(
        private formbuilder: UntypedFormBuilder,
        private translateService: TranslateService,
        private rightDefinitonsService: RightDefinitonsService,
        private usersService: UsersService,
        private spinner: NgxSpinnerService,
        private externalsParametersService: ExternalsParametersService,
        private dialog: MatDialog
    ) {
        this.rightsForm = this.formbuilder.group({
            rightsGroup: this.formbuilder.array([]),
        });

        // Abonnement à l'évènement du changement de langue au service de traduction
        this.subscriptionEvents = translateService.onLangChange.subscribe((e: Event) => {
            this.loadDatas();
        });
    }

    ngOnInit() {
        this.loadDatas();

        this.externalParametersChange = this.externalsParametersService.externalParametersChange$.subscribe((parameters) => {
            if (parameters) {
                this.externalParameters = parameters;
                this.isExternal = true;
                this.rights = this.rights.filter((a) => parameters.rights.includes(a.name));
            }
        });
    }

    ngOnDestroy() {
        this.subscriptionEvents.unsubscribe();
        this.externalParametersChange.unsubscribe();
    }

    loadDatas() {
        this.spinner.show();

        forkJoin({
            rightDefinitions: this.rightDefinitonsService.getRightDefinitions(),
            // Récupère les droits de l'utilisateur courant. Il n'est pas possible de donner des droits que l'utilisateur courant n'a pas.
            currentuser: this.usersService.getSessionCurrentUser(),
        }).subscribe(({ rightDefinitions, currentuser }) => {
            this.unassignedRights.splice(0, this.unassignedRights.length);

            this.availableRights.splice(0, this.availableRights.length);

            // Récupère la liste des définitions de droit disponible en fonction de l'affinité et des droits existants de l'utilisateur courant
            this.availableRights = this.getAvailableRightDefinitions(this.affinity, rightDefinitions, currentuser.rights);

            this.availableRights.forEach((r) => {
                let right = new RightItem();
                right.name = r.name;
                right.display =
                    r.display.length > 0
                        ? r.display.findIndex((d) => d.language === this.translateService.currentLang) !== -1
                            ? r.display.find((t) => t.language === this.translateService.currentLang).value
                            : r.name
                        : r.name;
                right.isSelected = false;

                // Affecte la liste des droits non utilisés
                this.unassignedRights.push(right);

                this.rightList = this.rightListControl.valueChanges.pipe(
                    startWith(null),
                    map((property: string | null) => (property ? this.filterProperty(property) : this.unassignedRights.slice()))
                );
            });

            const rightForm = [];

            this.rights
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((right) => {
                    // Supprime le droit  dela liste des droits non attribués
                    const indexUnassignedRight = this.unassignedRights.findIndex((operatingRight) => operatingRight.name === right.name);

                    if (indexUnassignedRight >= 0) {
                        this.unassignedRights.splice(indexUnassignedRight, 1);
                    }

                    // Récupère la définition de droit correspondant au droit
                    const rightDefinition = this.availableRights.find((u) => u.name === right.name);

                    if (rightDefinition) {
                        // Vérifie si l'utilisateur a le droit de modifier le droit
                        let noRightToModify = this.availableRights.findIndex((r) => r.name === right.name) === -1 ? true : false;

                        if (this.affinity === EnumAffinities.Member) {
                            rightForm.push(
                                this.formbuilder.group({
                                    name: [right.name],
                                    translateName: [this.getRightTranslate(right.name)],
                                    value: [
                                        {
                                            value: right.value,
                                            disabled: this.readonly || (right.legacy && rightDefinition.readonly) ? true : false,
                                        },
                                    ],
                                    readonly: [{ value: rightDefinition.readonly, disabled: true }],
                                    legacyable: [{ value: rightDefinition.legacyable, disabled: true }],
                                    legacy: [{ value: right.legacy, disabled: true }],
                                })
                            );
                        } else {
                            rightForm.push(
                                this.formbuilder.group({
                                    name: [right.name],
                                    translateName: [this.getRightTranslate(right.name)],
                                    value: [
                                        {
                                            value: right.value,
                                            disabled: this.readonly || (right.legacy && rightDefinition.readonly) || noRightToModify ? true : false,
                                        },
                                    ],
                                    readonly: [{ value: rightDefinition.readonly, disabled: true }],
                                    legacyable: [{ value: rightDefinition.legacyable, disabled: true }],
                                    legacy: [{ value: right.legacy, disabled: true }],
                                })
                            );
                        }
                    }
                });

            this.rightsForm = this.formbuilder.group({
                rightsGroup: this.formbuilder.array(rightForm),
            });

            // Tri la liste par nom
            this.unassignedRights.sort((a, b) => a.name.localeCompare(b.name));

            this.spinner.hide();
        });
    }

    /**
     * Lors de la modification d'un droit
     * @param rightName Nom du droit modifié
     */
    onChanges(rightName: string) {
        //this.spinner.show();

        // Application des changements des droits
        this.rights = [];

        const rights = this.rightsForm.getRawValue().rightsGroup as [{}];
        rights.forEach((right: Acl) => {
            right.value = right.value ? 1 : 0;

            // si le droit est modifié alors qu'il est hérité
            if (right.name === rightName && right.legacy) {
                // Modifie le droit en non hérité pour surcharger le droit hérité et permettre sa modification
                right.legacy = false;

                this.modifiedRights.push(right);
            } else {
                if (right.name === rightName && this.modifiedRights.findIndex((a) => a.name === rightName) > -1) {
                    right.legacy = true;

                    this.modifiedRights.splice(
                        this.modifiedRights.findIndex((a) => a.name === rightName),
                        1
                    );
                }
            }

            this.rights.push(right);
        });

        (<UntypedFormArray>this.rightsForm.controls['rightsGroup']).patchValue(this.rights);

        this.changed.emit(this.rights);

        //this.spinner.hide();
    }

    /** Click sur le bouton pour ajouter un droit */
    AddRight(rightsName: string[]) {
        this.spinner.show();

        rightsName.forEach((rightName) => {
            const rightsGroup = this.rightsForm.get('rightsGroup') as UntypedFormArray;

            // Récupère la définition de droit
            const rightDefinition = this.availableRights.find((r) => r.name === rightName);

            rightsGroup.push(
                this.formbuilder.group({
                    name: [rightName],
                    translateName: [this.getRightTranslate(rightName)],
                    value: [{ value: rightDefinition.default }],
                    readonly: [{ value: rightDefinition.readonly, disabled: true }],
                    legacyable: [{ value: rightDefinition.legacyable, disabled: true }],
                    legacy: [{ value: false, disabled: true }],
                })
            );

            const indexUnassignedRight = this.unassignedRights.findIndex((r) => r.name === rightName);

            if (indexUnassignedRight >= 0) {
                this.unassignedRights.splice(indexUnassignedRight, 1);
            }

            this.selectedRight = [];

            this.onChanges(rightName);
        });

        this.spinner.hide();
    }

    /** Click sur le bouton pour supprimer un droit */
    DeleteRight(rightName: string, index: number) {
        const rights = this.rightsForm.get('rightsGroup') as UntypedFormArray;

        rights.removeAt(index);

        const rightDefinition = this.availableRights.find((r) => r.name === rightName);

        if (rightDefinition) {
            let right = new RightItem();
            right.name = rightDefinition.name;
            right.display =
                rightDefinition.display.length > 0
                    ? rightDefinition.display.findIndex((a) => a.language === this.translateService.currentLang) !== -1
                        ? rightDefinition.display.find((t) => t.language === this.translateService.currentLang).value
                        : rightDefinition.name
                    : rightDefinition.name;
            right.isSelected = false;

            this.unassignedRights.push(right);
        }

        this.unassignedRights.sort((a, b) => a.name.localeCompare(b.name));

        this.onChanges(rightName);
    }

    getAvailableRightDefinitions(affinity: EnumAffinities, rightDefinitions: RightDefinition[], currentUserRights: Acl[]): RightDefinition[] {
        let rightDefinitionsFiltering: RightDefinition[];
        let avalaibleRightDefinitions: RightDefinition[] = [];
        switch (affinity) {
            case EnumAffinities.User:
                rightDefinitionsFiltering = rightDefinitions.filter(
                    (r) => (r.scope === EnumScopes.User || r.scope === EnumScopes.Managed) && (r.affinity === EnumAffinities.User || r.affinity === EnumAffinities.Any)
                );
                break;

            case EnumAffinities.Group:
                rightDefinitionsFiltering = rightDefinitions.filter(
                    (r) => (r.scope === EnumScopes.User || r.scope === EnumScopes.Managed) && (r.affinity === EnumAffinities.Group || r.affinity === EnumAffinities.Any)
                );
                break;

            case EnumAffinities.Organization:
                rightDefinitionsFiltering = rightDefinitions.filter(
                    (r) => (r.scope === EnumScopes.User || r.scope === EnumScopes.Managed) && (r.affinity === EnumAffinities.Organization || r.affinity === EnumAffinities.Any)
                );
                break;

            case EnumAffinities.Member:
                rightDefinitionsFiltering = rightDefinitions.filter((r) => (r.scope === EnumScopes.User || r.scope === EnumScopes.Managed) && r.affinity === EnumAffinities.Member);
                break;

            default:
                rightDefinitionsFiltering = rightDefinitions;
                break;
        }

        if (affinity === EnumAffinities.Member) {
            avalaibleRightDefinitions = rightDefinitionsFiltering;
        } else {
            currentUserRights.forEach((r) => {
                const rightDefinition = rightDefinitionsFiltering.find((rd) => rd.name === r.name);

                if (rightDefinition) {
                    avalaibleRightDefinitions.push(rightDefinition);
                }
            });
        }

        return avalaibleRightDefinitions;
    }

    getRightTranslate(rightName: string): string {
        try {
            return this.availableRights.find((r) => r.name === rightName).display.find((t) => t.language === this.translateService.currentLang).value;
        } catch {
            return rightName;
        }
    }

    resetSearch() {
        this.searchRight = '';
    }

    toggleAllSelection() {
        if (this.allSelected.selected) {
            // Ajoute tous les droits non assignés
            this.unassignedRights.map((ur) => ur.name).forEach((r) => this.selectedRight.push(r));
        } else {
            this.selectedRight = [];
        }
    }

    tosslePerOne() {
        if (this.allSelected.selected) {
            this.allSelected.deselect();
        }

        if (this.selectedRight.length === this.unassignedRights.length) {
            this.allSelected.select();
        }
    }

    toggleSelection(right: RightItem) {
        right.isSelected = !right.isSelected;
        if (right.isSelected) {
            this.selectedRight.push(right.name);
        } else {
            const i = this.selectedRight.findIndex((x) => x === right.name);
            this.selectedRight.splice(i, 1);
        }
    }

    private filterProperty(value: string): RightItem[] {
        const filterValue = value.toLowerCase();

        return this.unassignedRights.filter((right) => right.display.toLowerCase().indexOf(filterValue) >= 0);
    }

    AddItems() {
        this.AddRightsDialog = this.dialog.open(DialogAddRightsComponent, { panelClass: 'default-dialog', disableClose: true });
        this.AddRightsDialog.componentInstance.originRightList = this.rightList;

        this.AddRightsDialog.afterClosed()
            // .pipe(
            //     filter((result) => result === true),
            //     switchMap(() => this.applicationService.deleteApplication(applicationId))
            // )
            .subscribe({
                next: (result: RightItem[]) => {
                    if (result.length > 0) {
                        this.AddRight(
                            result.map((a) => {
                                return a.name;
                            })
                        );
                    }
                },
                error: () => {},
                complete: () => {
                    this.AddRightsDialog = null;
                },
            });
    }
}
